// Self-Healing Service for Pulsate Global
// Handles auto-recovery, health monitoring, and self-diagnosis

class SelfHealingService {
  constructor() {
    this.healthCheckInterval = null;
    this.wsReconnectAttempts = 0;
    this.maxWsReconnectAttempts = 10;
    this.healthStatus = {
      api: 'unknown',
      websocket: 'unknown',
      storage: 'unknown',
      network: 'unknown',
      lastCheck: null
    };
    this.listeners = new Set();
    this.errorLog = [];
    this.maxErrorLogSize = 100;
  }

  // Initialize the self-healing service
  init() {
    this.startHealthMonitoring();
    this.setupNetworkListener();
    this.setupStorageListener();
    this.setupGlobalErrorHandler();
    this.runDiagnostics();
    console.log('[SelfHealing] Service initialized');
  }

  // Subscribe to health status changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of status changes
  notifyListeners() {
    this.listeners.forEach(cb => cb(this.healthStatus));
  }

  // Start periodic health monitoring
  startHealthMonitoring() {
    this.healthCheckInterval = setInterval(() => {
      this.runDiagnostics();
    }, 30000); // Check every 30 seconds
  }

  // Stop health monitoring
  stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
  }

  // Run full diagnostics
  async runDiagnostics() {
    const diagnostics = {
      api: await this.checkApiHealth(),
      storage: this.checkStorageHealth(),
      network: this.checkNetworkHealth(),
      lastCheck: new Date().toISOString()
    };

    this.healthStatus = { ...this.healthStatus, ...diagnostics };
    this.notifyListeners();

    // Auto-fix issues
    await this.autoFix(diagnostics);

    return diagnostics;
  }

  // Check API health
  async checkApiHealth() {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
        signal: controller.signal
      });
      
      clearTimeout(timeout);
      return response.ok ? 'healthy' : 'degraded';
    } catch (error) {
      return 'unhealthy';
    }
  }

  // Check localStorage/sessionStorage health
  checkStorageHealth() {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      sessionStorage.setItem(testKey, testKey);
      sessionStorage.removeItem(testKey);
      return 'healthy';
    } catch (error) {
      return 'unhealthy';
    }
  }

  // Check network connectivity
  checkNetworkHealth() {
    return navigator.onLine ? 'healthy' : 'offline';
  }

  // Setup network change listener
  setupNetworkListener() {
    window.addEventListener('online', () => {
      this.healthStatus.network = 'healthy';
      this.notifyListeners();
      this.attemptReconnection();
    });

    window.addEventListener('offline', () => {
      this.healthStatus.network = 'offline';
      this.notifyListeners();
    });
  }

  // Setup storage quota listener
  setupStorageListener() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      setInterval(async () => {
        try {
          const estimate = await navigator.storage.estimate();
          const usagePercent = (estimate.usage / estimate.quota) * 100;
          
          if (usagePercent > 90) {
            this.cleanupStorage();
          }
        } catch (e) {}
      }, 60000);
    }
  }

  // Setup global error handler
  setupGlobalErrorHandler() {
    window.onerror = (message, source, lineno, colno, error) => {
      this.logError({
        type: 'uncaught',
        message,
        source,
        lineno,
        colno,
        stack: error?.stack
      });
      return false;
    };

    window.onunhandledrejection = (event) => {
      this.logError({
        type: 'unhandled_promise',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack
      });
    };
  }

  // Log errors
  logError(errorData) {
    const entry = {
      ...errorData,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    this.errorLog.unshift(entry);
    
    // Keep log size manageable
    if (this.errorLog.length > this.maxErrorLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxErrorLogSize);
    }

    // Send to backend
    this.reportError(entry);
  }

  // Report error to backend
  async reportError(errorData) {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/errors/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData)
      });
    } catch (e) {}
  }

  // Auto-fix detected issues
  async autoFix(diagnostics) {
    const fixes = [];

    // Fix storage issues
    if (diagnostics.storage === 'unhealthy') {
      fixes.push(this.fixStorage());
    }

    // Fix API issues - retry connection
    if (diagnostics.api === 'unhealthy' && diagnostics.network === 'healthy') {
      fixes.push(this.fixApiConnection());
    }

    await Promise.all(fixes);
  }

  // Fix storage issues
  async fixStorage() {
    try {
      // Try to clear unnecessary data
      this.cleanupStorage();
      
      // Verify fix
      const newStatus = this.checkStorageHealth();
      this.healthStatus.storage = newStatus;
      
      console.log('[SelfHealing] Storage fix attempted, status:', newStatus);
    } catch (e) {
      console.error('[SelfHealing] Storage fix failed:', e);
    }
  }

  // Clean up storage
  cleanupStorage() {
    try {
      // Remove old/temporary data
      const keysToKeep = ['token', 'user', 'theme', 'language', 'notifications_settings'];
      
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!keysToKeep.includes(key) && key?.startsWith('temp_')) {
          localStorage.removeItem(key);
        }
      }

      // Clear session storage except essential
      const sessionKeysToKeep = ['currentConversation'];
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (!sessionKeysToKeep.includes(key)) {
          sessionStorage.removeItem(key);
        }
      }

      console.log('[SelfHealing] Storage cleaned up');
    } catch (e) {}
  }

  // Fix API connection
  async fixApiConnection() {
    console.log('[SelfHealing] Attempting API reconnection...');
    
    // Try multiple times with backoff
    for (let i = 0; i < 3; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      const status = await this.checkApiHealth();
      
      if (status === 'healthy') {
        this.healthStatus.api = 'healthy';
        console.log('[SelfHealing] API reconnected successfully');
        return;
      }
    }
    
    console.log('[SelfHealing] API reconnection failed');
  }

  // Attempt WebSocket reconnection
  attemptReconnection() {
    if (this.wsReconnectAttempts < this.maxWsReconnectAttempts) {
      this.wsReconnectAttempts++;
      console.log(`[SelfHealing] WebSocket reconnection attempt ${this.wsReconnectAttempts}`);
      
      // Dispatch reconnection event
      window.dispatchEvent(new CustomEvent('wsReconnect'));
    }
  }

  // Reset WebSocket reconnection counter
  resetWsReconnectAttempts() {
    this.wsReconnectAttempts = 0;
  }

  // Get diagnostic report
  getDiagnosticReport() {
    return {
      healthStatus: this.healthStatus,
      errorLog: this.errorLog.slice(0, 10),
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        online: navigator.onLine,
        platform: navigator.platform
      },
      performanceMetrics: this.getPerformanceMetrics()
    };
  }

  // Get performance metrics
  getPerformanceMetrics() {
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      return {
        pageLoadTime: navigation?.loadEventEnd - navigation?.startTime,
        domContentLoaded: navigation?.domContentLoadedEventEnd - navigation?.startTime,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
        firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
      };
    } catch (e) {
      return {};
    }
  }
}

// Create singleton instance
const selfHealingService = new SelfHealingService();

export default selfHealingService;
